import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock } from '@phosphor-icons/react';
import PartnershipForm from '../../components/partnership/PartnershipForm';
import { hasPartnershipRequest, hasAccessGranted } from '../../firebase/partnershipService';
import HelpApiLayout from '../../components/layout/HelpApiLayout';
import ApiSidebar from '../../components/layout/ApiSidebar';
import './pages/ApiDocsPage.css';
import './DocsGate.css';

/**
 * Documentation Gate Component
 * Shows partnership form and checks access before allowing users to view documentation
 */
const DocsGate = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasRequest, setHasRequest] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    setIsChecking(true);

    try {
      // Check localStorage first for quick access
      const storedEmail = localStorage.getItem('partnership_request_email');
      const storedSubmitted = localStorage.getItem('partnership_request_submitted');

      if (storedEmail && storedSubmitted === 'true') {
        setEmail(storedEmail);
        
        // Check if they have been granted access
        const accessGranted = await hasAccessGranted(storedEmail);
        if (accessGranted) {
          setHasAccess(true);
          setHasRequest(true);
          setIsChecking(false);
          return;
        }

        // Check if they have submitted a request (but not yet approved)
        const requestExists = await hasPartnershipRequest(storedEmail);
        if (requestExists) {
          setHasRequest(true);
          setHasAccess(false);
          setIsChecking(false);
          return;
        }
      }

      // No stored data, show form
      setHasAccess(false);
      setHasRequest(false);
    } catch (error) {
      console.error('Error checking access:', error);
      // On error, show form (fail open for better UX)
      setHasAccess(false);
      setHasRequest(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleFormSuccess = () => {
    // After form submission, check access again
    checkAccess();
  };

  if (isChecking) {
    return (
      <HelpApiLayout type="api" sidebar={<ApiSidebar />}>
        <div className="api-docs-page">
          <div className="api-docs-page-container">
            <div className="api-docs-main">
              <div className="docs-gate-container">
                <div className="docs-gate-loading">
                  <div className="docs-gate-spinner"></div>
                  <p>Checking access...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HelpApiLayout>
    );
  }

  // If access is granted, show the children (documentation content)
  if (hasAccess) {
    return children;
  }

  // If request submitted but not approved, show pending message
  if (hasRequest && !hasAccess) {
    return (
      <HelpApiLayout type="api" sidebar={<ApiSidebar />}>
        <div className="api-docs-page">
          <div className="api-docs-page-container">
            <div className="api-docs-main">
              <div className="docs-gate-container">
                <div className="docs-gate-pending">
                  <div className="docs-gate-pending-icon">
                    <Clock size={64} weight="duotone" />
                  </div>
                  <h2 className="docs-gate-pending-title">Request Under Review</h2>
                  <p className="docs-gate-pending-message">
                    Thank you for your partnership request! We've received your submission and are reviewing it.
                    You'll receive an email notification once your access has been approved.
                  </p>
                  <p className="docs-gate-pending-note">
                    If you have any questions, please contact us at{' '}
                    <a href="mailto:vcharles@wesplit.io" className="docs-gate-link">
                      vcharles@wesplit.io
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HelpApiLayout>
    );
  }

  // Show partnership form
  return (
    <HelpApiLayout type="api" sidebar={<ApiSidebar />}>
      <div className="api-docs-page">
        <div className="api-docs-page-container">
          <div className="api-docs-main">
            <div className="docs-gate-container">
              <PartnershipForm onSuccess={handleFormSuccess} />
            </div>
          </div>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default DocsGate;
